import { useEffect, useState } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

interface ImageProps {
  image: Image
  classes?: string
  containerClasses?: string
  styles?: React.CSSProperties
  lazyLoad?: boolean
  priority?: 'high' | 'low'
}

const Image = ({
  image,
  classes,
  containerClasses,
  styles,
  lazyLoad,
  priority = 'low'
}: ImageProps) => {
  const hostImage = isDevelopment
    ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
    : ''

  const imgUrl = image?.asset?.sys?.uri
  const transformations = image?.transformations

  // Check if transformations aren't null and gets width from w= or leave blank
  const imgWidth = transformations
    ? transformations.match(/w=([^&]*)/)?.[1]
    : '900'

  // Check if transformations aren't null and gets height from h= or leave blank
  const imgHeight = transformations
    ? transformations.match(/h=([^&]*)/)?.[1]
    : '900'

  // Helper function to append parameters correctly
  const appendParams = (url: string, params: string) =>
    url.includes('?') ? `${url}&${params}` : `${url}?${params}`

  const lowResImg = appendParams(`${hostImage}${imgUrl}`, 'h=100&f=webp&q=10')
  const smImg = appendParams(
    `${hostImage}${imgUrl}`,
    `h=${Math.floor(Number(imgHeight) / 3)}&f=webp&q=80`
  )
  const mdImg = appendParams(
    `${hostImage}${imgUrl}`,
    `h=${Math.floor((Number(imgHeight) / 3) * 2)}&f=webp&q=80`
  )
  const lgImg = appendParams(
    `${hostImage}${imgUrl}`,
    `h=${imgHeight}&f=webp&q=80`
  )

  const [isLoaded, setIsLoaded] = useState(false)
  const [imageSrc, setImageSrc] = useState(lowResImg)

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.2,
    freezeOnceVisible: true
  })

  useEffect(() => {
    if (isIntersecting) {
      setIsLoaded(true)
      setImageSrc(lgImg)
    }
  }, [isIntersecting, lgImg])

  return (
    <picture
      ref={ref}
      className={cn(containerClasses, 'size-full')}
      style={styles || undefined}
    >
      <source media='(max-width: 320px)' srcSet={`${smImg} 240w`} />
      <source media='(max-width: 640px)' srcSet={`${mdImg} 480w`} />
      <source media='(max-width: 1280px)' srcSet={`${lgImg} 960w`} />
      <img
        src={imageSrc}
        alt={image?.altText}
        className={cn(
          'h-full w-full object-cover transition-opacity duration-300 ease-in-out',
          classes
            ? !isLoaded
              ? 'opacity-60 blur-md'
              : `${classes} ${'opacity-100'}`
            : ` ${isLoaded ? 'opacity-100' : 'opacity-60 blur-md'}`
        )}
        width={imgWidth}
        height={imgHeight}
        loading={lazyLoad ? 'lazy' : 'eager'}
        fetchPriority={priority === 'high' ? 'high' : 'low'}
      />
    </picture>
  )
}

export default Image
